import { REDEEM_V2_ABI } from '@lfg/abis';
import { useBoolean } from '@lfg/core/hooks';
import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { waitForTransactionReceipt, writeContract } from 'wagmi/actions';

import TransactionSubmittedToast from '@/components/TransactionSubmittedToast';
import { useGA, useWeb3 } from '@/hooks';
import { useClaimRedeemV2Mutation } from '@/store/services/redeem.service';
import { useFetchReferralStatsV2Query } from '@/store/services/referral.service';
import { useAppSelector } from '@/store/utils';
import { convertToHex, logError } from '@/utils/helpers';

import { RedeemHook, RedeemOptions } from '../types/redeem.type';
import { ClaimRedeemProps } from '../types/redeem.type';

const useRedeemV2 = (): RedeemHook => {
  const { config, address } = useWeb3();
  const { trackButtonClickEvent } = useGA();
  const { nodeId: nft } = useAppSelector(state => state.app);
  const { projectInfo } = useAppSelector(state => state.project);
  const [claimReemV2] = useClaimRedeemV2Mutation();
  const {
    isLoading: isLoadingReferralStats,
    data: referralStats,
    refetch: refetchReferralStats,
  } = useFetchReferralStatsV2Query({ address: address as string, nft, page: 'redeem' }, { skip: !address || !nft });
  const { value: isClaiming, setTrue: setIsClaimingTrue, setFalse: setIsClaimingFalse } = useBoolean(false);

  const handleClaimRedeem = useCallback(
    async ({ availableNode, nodeId, onSuccess }: ClaimRedeemProps) => {
      if (!address || !projectInfo || !projectInfo.referral.redeemAddress || !projectInfo?.programId) return;
      setIsClaimingTrue();
      trackButtonClickEvent({ name: 'mint', address });
      try {
        const nftAddr = nodeId ?? nft;
        const claimRes = await claimReemV2({ nft: nftAddr, address });
        if (!claimRes.data || Object.keys(claimRes.data.signature).length === 0) {
          toast.error('Claim redeem failed. Please try again or contact to support');
          return;
        }
        const {
          nonce,
          signature: { v, s, r },
        } = claimRes.data;
        const hash = await writeContract(config, {
          abi: REDEEM_V2_ABI,
          address: projectInfo.referral.redeemAddress as `0x${string}`,
          functionName: 'redeem',
          args: [
            convertToHex(`0000000${projectInfo.programId}`),
            address,
            availableNode,
            RedeemOptions.ONLY_MINT,
            nonce,
            v,
            r,
            s,
          ],
        });
        toast(TransactionSubmittedToast());
        await waitForTransactionReceipt(config, { hash });
        toast.success('Claimed redeem successfully');
        refetchReferralStats();
        onSuccess?.();
      } catch (error: unknown) {
        const errorDetails = logError(error, 'handleRedeem');
        const errorMessage = errorDetails.message || (error instanceof Error ? error.message : 'Claim redeem failed');
        toast.error(errorMessage);
        throw error;
      } finally {
        setIsClaimingFalse();
      }
    },
    [address, config, nft, projectInfo, claimReemV2, setIsClaimingFalse, setIsClaimingTrue, refetchReferralStats],
  );

  return {
    referralStats,
    isClaiming,
    isLoadingReferralStats,
    handleClaimRedeem,
  };
};

export { useRedeemV2 };
